<template>
  <section>
    <div class="mb-3 flex h-5 items-center justify-between">
      <p class="text-text-neutral-tertiary text-xs">{{ passengerKeyTitle }}</p>
      <button
        v-if="isClearButtonVisible"
        type="button"
        class="text-text-positive-primary text-sm font-semibold"
        @click="clearPassenger"
      >
        {{ t('general.clear') }}
      </button>
    </div>
    <PassengersDataForm
      source-page="passengersData"
      :is-domestic="isDomestic"
      :passenger="passenger"
      :validations="validations"
      :provider-class="providerClass"
      @onPassengerUpdate="emit('onPassengerUpdate', $event)"
    />
    <div
      v-if="hasAirlineBonusCardsSchema"
      class="bg-background-neutral-secondary mt-3 space-y-2 rounded-2xl px-4 py-3"
    >
      <label class="flex items-center justify-between">
        <span class="text-text-neutral-primary text-sm font-medium">
          {{ t('passengersData.milesCard') }}
        </span>
        <FreeSwitch
          v-model="hasMilesCard"
          @update:model-value="onToggleMilesCard"
        />
      </label>
      <FreeInput
        v-if="hasMilesCard"
        v-model="milesCardNumber"
        type="number"
        :placeholder="t('passengersData.cardNumber')"
        :has-error="isMilesCardRequiredError"
        @update:model-value="onBonusCardUpdate"
      >
        <template #errorMessage>
          {{ t('passengersData.validations.required') }}
        </template>
      </FreeInput>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { isEqual } from 'lodash-es';
  import { PassengersDataForm, FreeInput, FreeSwitch } from 'freedom-shared';
  import type { Validation } from '@vuelidate/core';
  import type { passengerValidation } from 'shared';
  import type { PassengerKey } from '@aviata/booking-data';

  import { formPassengerConfig } from '@/entities/form-passenger';

  import { milesCardsStorageLib } from '../lib';
  import type { PassengerWithDocType } from '../types';

  const props = defineProps<{
    isDomestic: boolean;
    passenger: PassengerWithDocType;
    passengerKey: PassengerKey;
    validations: Validation<passengerValidation.types.PassengerValidation>;
    providerClass?: string;
    airlineBonusCardsSchema?: Record<string, unknown>;
    isSubmitAttempted?: boolean;
  }>();

  const emit = defineEmits<{
    onPassengerUpdate: [PassengerWithDocType];
    onMilesCardStateUpdate: [
      { hasMilesCard: boolean; milesCardNumber: string },
    ];
  }>();

  const { t } = useI18n();

  const passengerKeyTitle = t(
    `passengersData.passengerTypes.${props.passengerKey.slice(0, 3)}.text`,
  );

  const getCardValue = () => {
    if (props.passenger.id) {
      const storedValue = milesCardsStorageLib.getMilesCardNumberByPassengerId(
        props.passenger.id,
      );
      if (storedValue) {
        return storedValue;
      }
    }

    return '';
  };

  const milesCardNumber = ref(getCardValue());
  const hasMilesCard = ref(!!milesCardNumber.value);
  const hasMilesCardError = ref(false);
  const hasAirlineBonusCardsSchema = computed(
    () =>
      !!props.airlineBonusCardsSchema &&
      Object.keys(props.airlineBonusCardsSchema).length > 0,
  );

  const isClearButtonVisible = computed(
    () => !isEqual(props.passenger, formPassengerConfig.emptyPassenger),
  );
  const isMilesCardRequiredError = computed(() => {
    return (
      !!props.isSubmitAttempted &&
      hasMilesCard.value &&
      !milesCardNumber.value.trim()
    );
  });

  const clearPassenger = () =>
    emit('onPassengerUpdate', formPassengerConfig.emptyPassenger);

  const onToggleMilesCard = (value: boolean) => {
    emit('onMilesCardStateUpdate', {
      hasMilesCard: value,
      milesCardNumber: milesCardNumber.value,
    });

    if (!value) {
      milesCardNumber.value = '';
      return;
    }

    const storedValue = getCardValue();
    milesCardNumber.value = storedValue;
  };

  const onBonusCardUpdate = (value: string) => {
    emit('onMilesCardStateUpdate', {
      hasMilesCard: hasMilesCard.value,
      milesCardNumber: value,
    });
  };
</script>
